import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { GVAUserDTO, GVAUserPrivilegedDTO } from "../dtos/GVAUserDTO";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import _ from "lodash";
import { computed } from "vue";
const USER_STALE_TIME = STALE_TIME_MEDIUM;
export function useMyUser() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["user-me"],
        queryFn: async () => (await seedgreenAPI.get("v2/User/me", { maxRedirects: 5 })).data,
        staleTime: USER_STALE_TIME,
        select: (data) => {
            try {
                if (!data)
                    throw new Error("User not found");
                return new GVAUserPrivilegedDTO(data);
            }
            catch (error) {
                console.error("query/user::select ERROR", error);
            }
        },
    });
    return {
        myUser: query.data,
        ...query,
    };
}
export function useUser(userId) {
    const tokenValid = useAuthorizedQuery();
    const query = useQuery({
        enabled: computed(() => tokenValid.value && !!userId.value),
        queryKey: ["user", userId],
        queryFn: async () => (await seedgreenAPI.get(`v2/User/${userId.value}`, {
            maxRedirects: 5,
        })).data,
        staleTime: USER_STALE_TIME,
        select: (data) => {
            try {
                if (!data)
                    throw new Error("User not found");
                if (data.userName) {
                    return new GVAUserPrivilegedDTO(data);
                }
                return new GVAUserDTO(data);
            }
            catch (error) {
                console.error("query/user::select ERROR", error);
            }
        },
    });
    return {
        user: query.data,
        ...query,
    };
}
export function useUsers() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["users"],
        queryFn: async () => (await seedgreenAPI.get("v2/User")).data,
        staleTime: USER_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((user) => new GVAUserDTO(user)) ?? []);
            }
            catch (error) {
                console.error("query/users select ERROR", error);
            }
        },
    });
    const users = query.data;
    const usersById = computed(() => _.keyBy(users.value ?? [], "id"));
    const sortedNonInternalUsers = computed(() => _.sortBy((users.value ?? []).filter((u) => !u.internalUser), "name"));
    return {
        users,
        usersById,
        sortedNonInternalUsers,
        ...query,
    };
}
export function useUsersPrivileged() {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["users-privileged"],
        queryFn: async () => (await seedgreenAPI.get("v2/User/Privileged", { maxRedirects: 5 }))
            .data,
        staleTime: USER_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((user) => new GVAUserPrivilegedDTO(user)) ?? []);
            }
            catch (error) {
                console.error("query/users-privileged select ERROR", error);
            }
        },
    });
    return {
        usersPrivileged: query.data,
        ...query,
    };
}
export function useUserPermissionsFromRoles(userId) {
    const query = useQuery({
        enabled: useAuthorizedQuery(),
        queryKey: ["user-roles-permissions", userId],
        queryFn: async () => (await seedgreenAPI.get(`v2/User/${userId.value}/PermissionsFromRoles`, { maxRedirects: 5 })).data,
        staleTime: USER_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((permission) => permission) ?? []);
            }
            catch (error) {
                console.error("query/user-roles-permissions select ERROR", error);
            }
        },
    });
    return {
        permissionsFromRoles: query.data,
        ...query,
    };
}
export function useSetCustomer() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (customerId) => {
            await seedgreenAPI.post(`v2/User/Me/Customer/${customerId}`);
        },
        onSuccess: async () => {
            // All (most) queries are now technically invalid
            await queryClient.resetQueries();
        },
    });
    return {
        setCustomer: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetUserActive() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (args) => {
            if (args.active) {
                await seedgreenAPI.post(`v2/User/${args.userId}/Activate`);
            }
            else {
                await seedgreenAPI.post(`v2/User/${args.userId}/Deactivate`);
            }
            return args.userId;
        },
        onSuccess: async (userId) => {
            await queryClient.invalidateQueries({ queryKey: ["users"] });
            await queryClient.invalidateQueries({
                queryKey: ["users-privileged"],
            });
            await queryClient.invalidateQueries({ queryKey: ["user", userId] });
        },
    });
    return {
        setUserActive: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetUserAdmin() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (args) => {
            await seedgreenAPI.put(`v2/User/${args.userId}/Admin`, args.admin, {
                headers: { "Content-Type": "application/json" },
            });
            return args.userId;
        },
        onSuccess: async (userId) => {
            await queryClient.invalidateQueries({ queryKey: ["users"] });
            await queryClient.invalidateQueries({
                queryKey: ["users-privileged"],
            });
            await queryClient.invalidateQueries({ queryKey: ["user", userId] });
        },
    });
    return {
        setUserAdmin: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetUserPermissions() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (args) => {
            await seedgreenAPI.put(`v2/User/${args.userId}/Permissions`, args.permissions);
            return args.userId;
        },
        onSuccess: async (userId) => {
            await queryClient.invalidateQueries({
                queryKey: ["users-privileged"],
            });
            await queryClient.invalidateQueries({ queryKey: ["user", userId] });
        },
    });
    return {
        setUserPermissions: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetUserRoles() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (args) => {
            await seedgreenAPI.put(`v2/User/${args.userId}/Roles`, args.roles);
            return args.userId;
        },
        onSuccess: async (userId) => {
            await queryClient.invalidateQueries({
                queryKey: ["users-privileged"],
            });
            await queryClient.invalidateQueries({ queryKey: ["user", userId] });
            await queryClient.invalidateQueries({
                queryKey: ["user-roles-permissions", userId],
            });
        },
    });
    return {
        setUserRoles: mutation.mutateAsync,
        ...mutation,
    };
}
export function useSetUserLocale() {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (locale) => {
            await seedgreenAPI.put(`v2/User/Locale`, locale, {
                headers: { "Content-Type": "application/json" },
            });
        },
        onSuccess: async (userId) => {
            await queryClient.invalidateQueries({
                queryKey: ["users-privileged"],
            });
            await queryClient.invalidateQueries({ queryKey: ["user", userId] });
        },
    });
    return {
        setUserLocale: mutation.mutateAsync,
        ...mutation,
    };
}
/**
 * Mutation to check username availability in bulk.
 * Used to validate usernames before creating invites.
 * Returns a dictionary mapping usernames to their availability status.
 */
export function useCheckUsernameAvailability() {
    const isAuthorized = useAuthorizedQuery();
    const mutation = useMutation({
        mutationFn: async (usernames) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/User/CheckUsernameAvailability", { usernames });
            return response.data;
        },
    });
    return {
        checkUsernameAvailability: mutation.mutateAsync,
        isLoading: mutation.isPending,
        error: mutation.error,
    };
}
